import {
  Clock, User, Package, Edit3, CheckCircle, AlertTriangle, Pause, FileText, PlusCircle,
  Workflow, CalendarClock, Flag, ClipboardCheck, Activity, Hash, PoundSterling, Tag, Gauge, Cog,
} from 'lucide-react';
import { useFormatters } from '../../contexts/PreferencesContext';

export interface ChangelogEntry {
  id: number;
  change_date: string;
  changed_by: string;
  changed_by_company?: string;
  // CR-4713: When the action was performed via impersonation, these surface
  // the real admin user (and their company) alongside the impersonated
  // changed_by. acting_as_user_details is the nested user object from the
  // backend's UserSerializer; acting_as_company is the resolved name.
  acting_as_user_details?: { id: number; first_name?: string; last_name?: string; full_name?: string; email?: string } | null;
  acting_as_company?: string | null;
  // CR-4713 round 4: company_type lets the renderer say "on behalf of
  // customer" / "on behalf of supplier" instead of the impersonator's
  // company name ("Helix Platform"), which was confusing to reviewers.
  company_type?: 'supplier' | 'customer' | '';
  field_changed: string;
  old_value: string | null;
  new_value: string;
  reason?: string;
  change_type?: string;
  event_type?: string;
  // CR-4865: shared across rows that belong to the same PO change request
  // (proposal + decision). Null for pre-CR-4865 rows — the change-request
  // tab falls back to the legacy ungrouped renderer for those.
  change_request_ref?: number | null;
}

interface ChangelogProps {
  entries: ChangelogEntry[];
  maxHeight?: string;
  emptyMessage?: string;
}

/**
 * Reasons that are machine bookkeeping rather than an explanation.
 *
 * ERP-import rows carry the import run's job id as their reason ("… import
 * job 11cf33f1-4c00-46ca-af94-5eacf96bcc5e"). It identifies a batch for
 * support, means nothing to the person reading the history, and repeats on
 * every imported row — the actor line already says the change came from a
 * system import. Matched by shape, not by name, so it holds for any tenant.
 */
const OPAQUE_REASON_PATTERN = /\bimport job\s+[0-9a-f-]{8,}/i;

function isReadableReason(reason?: string): boolean {
  return !!reason && !OPAQUE_REASON_PATTERN.test(reason);
}

export default function Changelog({ entries, maxHeight = '550px', emptyMessage = 'No audit history available' }: ChangelogProps) {
  const { formatDate: contextFormatDate } = useFormatters();

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return contextFormatDate(dateString);
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    // Skip time rendering for date-only strings (YYYY-MM-DD / DD/MM/YYYY)
    // where the 00:00 UTC -> 01:00 BST conversion produces a meaningless
    // "01:00" next to every GRN receipt.
    if (!dateString.includes('T') && !dateString.includes(':')) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const FIELD_LABELS: Record<string, string> = {
    delivery_date: 'Supplier Promise Date',
    supplier_promise_date: 'Supplier Promise Date',
    original_promise_date: 'Original promise date',
    acknowledge_status: 'Acknowledge status',
    on_hold: 'Hold status',
    progress: 'Progress update',
    is_critical: 'Critical',
    unit_price: 'Unit price',
    quantity: 'Quantity',
    received_qty: 'Shipped qty',
    workflow_status: 'GRN status',
    status: 'Status',
    ncr_raised: 'NCR raised',
    otd_override: 'OTD override',
    job_event: 'Job',
  };

  const EVENT_TYPE_CONFIG: Record<string, { label: string; badgeClass: string; dotClass: string; Icon: React.ComponentType<{ className?: string }> }> = {
    grn: { label: 'GRN', badgeClass: 'bg-blue-100 text-blue-800', dotClass: 'bg-blue-500', Icon: Package },
    asn: { label: 'ASN', badgeClass: 'bg-purple-100 text-purple-800', dotClass: 'bg-purple-500', Icon: FileText },
    // The order line's own processing stage. Shares the cyan Workflow
    // treatment FIELD_ICON_CONFIG gives the same field, so a stage change
    // reads the same whether or not an ASN triggered it.
    order: { label: 'Order', badgeClass: 'bg-cyan-100 text-cyan-800', dotClass: 'bg-cyan-500', Icon: Workflow },
    ncr: { label: 'NCR', badgeClass: 'bg-red-100 text-red-800', dotClass: 'bg-red-600', Icon: AlertTriangle },
    hold: { label: 'Hold', badgeClass: 'bg-orange-100 text-orange-800', dotClass: 'bg-orange-500', Icon: Pause },
    release: { label: 'Release', badgeClass: 'bg-emerald-100 text-emerald-800', dotClass: 'bg-emerald-500', Icon: CheckCircle },
    progress: { label: 'Progress', badgeClass: 'bg-sky-100 text-sky-800', dotClass: 'bg-sky-500', Icon: Clock },
    change_request: { label: 'Change Request', badgeClass: 'bg-amber-100 text-amber-800', dotClass: 'bg-amber-500', Icon: Edit3 },
    change_accepted: { label: 'Change Accepted', badgeClass: 'bg-green-100 text-green-800', dotClass: 'bg-green-500', Icon: CheckCircle },
    created: { label: 'Created', badgeClass: 'bg-slate-100 text-slate-700', dotClass: 'bg-slate-500', Icon: PlusCircle },
    // Manufacturing job events merged in from the ERP's own amendment log.
    job: { label: 'Job', badgeClass: 'bg-teal-100 text-teal-800', dotClass: 'bg-teal-500', Icon: Cog },
    field_change: { label: '', badgeClass: '', dotClass: 'bg-gray-400', Icon: Edit3 },
  };

  // Per-field icons for generic field changes so the timeline reads at a
  // glance instead of a wall of identical pencils. Only used when the entry
  // isn't a richer event type (grn/asn/hold/etc., which keep their own icon).
  const FIELD_ICON_CONFIG: Record<string, { Icon: React.ComponentType<{ className?: string }>; dotClass: string }> = {
    processing_stage:      { Icon: Workflow,      dotClass: 'bg-cyan-500' },
    status:                { Icon: Activity,      dotClass: 'bg-violet-500' },
    acknowledge_status:    { Icon: ClipboardCheck, dotClass: 'bg-violet-500' },
    supplier_promise_date: { Icon: CalendarClock, dotClass: 'bg-indigo-500' },
    delivery_date:         { Icon: CalendarClock, dotClass: 'bg-indigo-500' },
    original_promise_date: { Icon: CalendarClock, dotClass: 'bg-indigo-500' },
    sla_delivery_date:     { Icon: CalendarClock, dotClass: 'bg-indigo-500' },
    mrp_scheduled_date:    { Icon: CalendarClock, dotClass: 'bg-indigo-500' },
    on_hold:               { Icon: Pause,         dotClass: 'bg-orange-500' },
    otd_override:          { Icon: Gauge,         dotClass: 'bg-teal-500' },
    priority:              { Icon: Flag,          dotClass: 'bg-amber-500' },
    quantity:              { Icon: Hash,          dotClass: 'bg-blue-500' },
    received_qty:          { Icon: Package,       dotClass: 'bg-blue-500' },
    outstanding_qty:       { Icon: Package,       dotClass: 'bg-blue-500' },
    unit_price:            { Icon: PoundSterling, dotClass: 'bg-emerald-500' },
    total_price:           { Icon: PoundSterling, dotClass: 'bg-emerald-500' },
    part_number:           { Icon: Tag,           dotClass: 'bg-slate-500' },
    description:           { Icon: FileText,      dotClass: 'bg-slate-500' },
  };

  const getEventConfig = (entry: ChangelogEntry) => {
    // CR-4731: Show "Change Accepted" (green) for accepted changes
    const reason = (entry.reason || '').toLowerCase();
    if (
      entry.event_type === 'change_request' &&
      (entry.new_value === 'accepted' || reason.includes('accepted'))
    ) {
      return EVENT_TYPE_CONFIG['change_accepted'];
    }
    if (entry.field_changed === 'supplier_promise_date' && reason.includes('accepted')) {
      return EVENT_TYPE_CONFIG['change_accepted'];
    }
    // Differentiate Hold vs Release — same event_type='hold' from the
    // serializer but the transition direction matters (going on-hold
    // should stay orange; coming off-hold should render as green
    // "Release").
    if (entry.event_type === 'hold') {
      const newVal = (entry.new_value || '').toLowerCase();
      if (newVal && newVal !== 'on hold' && newVal !== 'hold') {
        return EVENT_TYPE_CONFIG['release'];
      }
    }
    const type = entry.event_type || 'field_change';
    const base = EVENT_TYPE_CONFIG[type] || EVENT_TYPE_CONFIG['field_change'];
    // For generic field changes, swap in a field-relevant icon/colour.
    if (base === EVENT_TYPE_CONFIG['field_change']) {
      const fieldCfg = FIELD_ICON_CONFIG[entry.field_changed];
      if (fieldCfg) return { ...base, ...fieldCfg };
    }
    return base;
  };

  const formatFieldName = (field: string) => {
    if (FIELD_LABELS[field]) return FIELD_LABELS[field];
    return field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // CR-4680: Human-readable labels for acknowledge_status values
  const ACK_STATUS_DISPLAY: Record<string, string> = {
    new: 'New',
    accepted: 'Accepted',
    change_proposed_customer: 'Proposed by Supplier',
    change_proposed_supplier: 'Proposed by Customer',
    change_declined: 'Declined',
    declined: 'Declined',
    wip: 'Work In Progress',
    despatched: 'Despatched',
    cancelled: 'Cancelled',
    closed: 'Closed',
  };

  const formatEntryValue = (field: string, value: string): string => {
    if (field === 'acknowledge_status') return ACK_STATUS_DISPLAY[value] || value;
    return value;
  };

  return (
    <div className="card card-custom h-full">
      <div className="card-header border-b px-4 py-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Audit History
        </h3>
      </div>
      <div className="card-body p-4">
        {entries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          <div className="timeline-container" style={{ overflowY: 'auto', maxHeight }}>
            <div className="space-y-6">
              {entries.map((entry, index) => {
                const eventConfig = getEventConfig(entry);
                return (
                <div key={entry.id || index} className="timeline-item flex gap-3">
                  {/* Timeline Label - Date/Time */}
                  <div className="timeline-label min-w-[100px] text-sm">
                    <div className="font-semibold text-dark">{formatDate(entry.change_date)}</div>
                    <div className="text-muted-foreground">{formatTime(entry.change_date)}</div>
                  </div>

                  {/* Timeline Badge */}
                  <div className="timeline-badge flex flex-col items-center">
                    <div className={`w-6 h-6 rounded-full ${eventConfig.dotClass} flex items-center justify-center text-white shadow-sm`}>
                      <eventConfig.Icon className="h-3.5 w-3.5" />
                    </div>
                    {index < entries.length - 1 && (
                      <div className="w-0.5 flex-1 bg-border mt-1"></div>
                    )}
                  </div>

                  {/* Timeline Content */}
                  <div className="timeline-content flex-1 pb-4">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="font-semibold text-dark">
                        {/* CR-4731: Show "Change Accepted" for accepted changes */}
                        {/* CR-5282: 'created' rows are an event, not a field
                            change — the "Created" badge already conveys the
                            kind, so the title reads as the entity that was
                            created instead of duplicating the badge label. */}
                        {entry.field_changed === 'created'
                          ? 'PO Line'
                          : entry.field_changed === 'acknowledge_status' && entry.new_value === 'accepted'
                          ? 'Change Accepted'
                          : formatFieldName(entry.field_changed)}
                      </span>
                      {eventConfig.label && (
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${eventConfig.badgeClass}`}>
                          {eventConfig.label}
                        </span>
                      )}
                    </div>

                    {entry.old_value && entry.new_value ? (
                      <div className="text-dark font-medium">
                        {formatEntryValue(entry.field_changed, entry.old_value)} → {formatEntryValue(entry.field_changed, entry.new_value)}
                      </div>
                    ) : entry.new_value ? (
                      <div className={`text-dark ${entry.event_type === 'grn' ? 'whitespace-pre-line text-sm leading-relaxed' : 'font-medium'}`}>
                        {entry.new_value}
                      </div>
                    ) : null}

                    {/* Inline fill bar for GRN receipts — shows the schedule
                        filling up over time. Parses "X of Y" from the new_value
                        text, e.g. "3.000 of 8.000". */}
                    {entry.event_type === 'grn' && entry.new_value && (() => {
                      const match = entry.new_value.match(/([0-9.]+)\s+of\s+([0-9.]+)/);
                      if (!match) return null;
                      const filled = parseFloat(match[1]);
                      const total = parseFloat(match[2]);
                      if (!total || total <= 0) return null;
                      const pct = Math.min(100, (filled / total) * 100);
                      return (
                        <div className="mt-2 w-full max-w-xs h-2 bg-gray-100 rounded overflow-hidden border border-gray-200">
                          <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      );
                    })()}

                    {isReadableReason(entry.reason) && (
                      <div className="text-muted-foreground text-sm mt-1">
                        {entry.reason}
                      </div>
                    )}

                    <div className="text-muted-foreground text-sm mt-1 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {entry.changed_by_company ? `${entry.changed_by_company} · ` : ''}{entry.changed_by}
                      {/* CR-4713 round 4: Jacqueline rejected the earlier
                          "(acting as Helix Platform · Sarah Gleeson)" phrasing —
                          the impersonator's company name ("Helix Platform") was
                          confusing and the wording didn't read naturally. The
                          agreed format is "(on behalf of <side> – <Name>)"
                          where <side> is customer/supplier taken from the
                          entry's company_type (the impersonated session's
                          role). If company_type is missing we fall back to
                          just the name so the audit row still carries the
                          impersonator's identity. */}
                      {entry.acting_as_user_details && (() => {
                        const a = entry.acting_as_user_details;
                        const name = a.full_name || [a.first_name, a.last_name].filter(Boolean).join(' ') || a.email || `User #${a.id}`;
                        const side = entry.company_type === 'customer'
                          ? 'customer'
                          : entry.company_type === 'supplier'
                            ? 'supplier'
                            : null;
                        return (
                          <span className="ml-1 italic">
                            (on behalf of {side ? `${side} – ` : ''}{name})
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
